import { notFound } from '@tanstack/react-router'
import { z } from 'zod'

import { type LoaderContext } from '@/@types/loader-context'
import PendingComponent from '@/components/pending-component'
import { isOuisenseUser } from '@/hooks/use-is-ouisense-user'
import { siteModuleSynopticsQuery } from '@/queries/site-module-synoptics.query'
import { synopticModulesQuery } from '@/queries/synoptic-modules.query'

import { type ModuleSynopticDomain } from './module-synoptic-domain'

// Options de route des pages de synoptiques à modules, paramétrées par le domaine (cf.
// ModuleSynopticDomain). Les arbres de routes des catégories sont jumeaux — mêmes params, même
// search, mêmes gardes, mêmes préchargements : les décliner à la main les ferait diverger, et une
// correction de garde posée sur un arbre pourrait manquer l'autre. Chaque fichier de route ne garde
// donc que son littéral de chemin et l'appel à la fabrique qui le concerne.

// `synoptic` désigne le dernier synoptique atteint par les liens du treegraph : la card de son
// bâtiment l'affiche, les autres bâtiments gardent leur sélection propre (cf. BuildingSynopticCard).
// Absent, ou hors du site : chaque bâtiment affiche son premier synoptique.
export const SearchSchema = z.object({
  synoptic: z.number().int().positive().optional(),
})

export type Search = z.infer<typeof SearchSchema>

// `id` est coercé en number par le parseParams de la route parente `_dashboard.site.$id` : les
// loaders ci-dessous le reçoivent donc déjà typé, sans annoter leurs params — les annoter romprait
// l'inférence du routeur, qui déduit params et search du littéral de chemin de chaque route.
type SiteParams = { params: { id: number } }

// Garde des actes d'intégration (gestion, génération, édition) : réservés aux collaborateurs
// OuiSense, en miroir de ouisenseOnlyMiddleware côté API (les écritures répondraient 403). 404 plutôt
// que 403, pour ne pas révéler la page à un utilisateur client qui forge l'URL. Reposée par CHAQUE
// route concernée : beforeLoad ne cascade pas d'une route sœur à l'autre (routes à plat, pas de
// layout commun).
function ouisenseOnly({ context }: { context: LoaderContext }) {
  if (!isOuisenseUser(context.user)) {
    throw notFound()
  }
}

// Vue technique : liste des synoptiques du site et catalogue de modules. Le schéma affiché est
// regénéré à chaque chargement depuis les modules à jour (cf. index.lazy), la page suspend donc sur
// les deux requêtes — précharger la seule liste laisserait la seconde suspendre jusqu'au boundary
// parent au lieu d'afficher l'état de chargement de la route.
export function technicalRouteOptions(domain: ModuleSynopticDomain) {
  return {
    validateSearch: SearchSchema,
    loader({ context, params }: { context: LoaderContext } & SiteParams) {
      return Promise.all([
        context.queryClient.ensureQueryData(siteModuleSynopticsQuery(domain.category, params.id)),
        context.queryClient.ensureQueryData(synopticModulesQuery(domain.category)),
      ])
    },
    pendingComponent: PendingComponent,
  }
}

// Gestion des synoptiques du domaine.
export function manageRouteOptions(domain: ModuleSynopticDomain) {
  return {
    beforeLoad: ouisenseOnly,
    loader({ context, params }: { context: LoaderContext } & SiteParams) {
      return context.queryClient.ensureQueryData(siteModuleSynopticsQuery(domain.category, params.id))
    },
    pendingComponent: PendingComponent,
  }
}

// Génération d'un synoptique : le catalogue de modules est lu en useSuspenseQuery par la page.
export function generateRouteOptions(domain: ModuleSynopticDomain) {
  return {
    beforeLoad: ouisenseOnly,
    loader({ context }: { context: LoaderContext }) {
      return context.queryClient.ensureQueryData(synopticModulesQuery(domain.category))
    },
    pendingComponent: PendingComponent,
  }
}

// Édition d'un synoptique : le synoptique édité vient de la liste scopée (pas de GET unitaire
// ouvert), un id inconnu ou hors scope n'y figure pas → notFound. Le catalogue de modules est
// préchargé AVEC elle : le formulaire de composition le lit lui aussi en useSuspenseQuery (cf.
// BoilerRoomForm), et l'omettre ferait suspendre la page jusqu'au boundary parent.
export function editRouteOptions(domain: ModuleSynopticDomain) {
  return {
    parseParams(params: { synopticId: string }) {
      const synopticId = Number.parseInt(params.synopticId, 10)

      if (Number.isNaN(synopticId)) {
        throw notFound()
      }

      return { synopticId }
    },
    beforeLoad: ouisenseOnly,
    async loader({ context, params }: { context: LoaderContext; params: { id: number; synopticId: number } }) {
      const [synoptics] = await Promise.all([
        context.queryClient.ensureQueryData(siteModuleSynopticsQuery(domain.category, params.id)),
        context.queryClient.ensureQueryData(synopticModulesQuery(domain.category)),
      ])

      if (!synoptics.some((synoptic) => synoptic.id === params.synopticId)) {
        throw notFound()
      }

      return synoptics
    },
    pendingComponent: PendingComponent,
  }
}
